import {
  Button,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./FileUploader.module.scss";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { RemoveIcon } from "../../assets/icons";
import { error } from "console";

export interface FileWrapper {
  language?: string;
  file: File;
}

interface FileUploaderProps {
  title?: string | undefined;
  allowedExtensions?: string[];
  onChange: (file: File | FileWrapper[]) => void;
  isReadOnly: boolean;
  appendMode?: boolean;
  prefix?: string;
  customClassName?: string;
  errorValidate?: boolean | null | undefined;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  title,
  allowedExtensions,
  onChange,
  isReadOnly,
  appendMode,
  prefix,
  customClassName,
  errorValidate,
}) => {
  const { t } = useTranslation();

  const [dragActive, setDragActive] = useState(false);
  const [errorValidation, setErrorValidation] = useState<string | null>(null);

  const [selectedFiles, setSelectedFiles] = useState<FileWrapper[] | null>();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    setDragActive(false);
    if (!isReadOnly && e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleChangeFiles(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: any) => {
    e.preventDefault();
    if (!isReadOnly && e.target.files && e.target.files[0]) {
      handleChangeFiles(e.target.files[0]);
    }
  };

  const handleChangeFiles = (file: File) => {
    if (validateExtension(file)) {
      setErrorValidation(null);

      const newFile: FileWrapper = {
        language: prefix,
        file: file,
      };

      if (appendMode) {
        const arrayValues = [...(selectedFiles || [])];

        const previousKey = arrayValues?.findIndex(
          (x) => x.language === newFile.language
        );
        if (previousKey > -1) arrayValues.splice(previousKey, 1);

        arrayValues.push(newFile);

        setSelectedFiles(arrayValues);
        onChange(arrayValues);
      } else {
        setSelectedFiles([newFile]);
        onChange(file);
      }

      if (inputRef && inputRef.current) inputRef.current.value = "";
    }
  };

  const onButtonClick = () => {
    if (inputRef.current != null && !isReadOnly) inputRef.current.click();
  };

  const onRemoveClick = (index: number) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const aux = [...selectedFiles];
      aux.splice(index, 1);

      setSelectedFiles(aux);
      onChange(aux);
    }
  };

  const validateExtension = (file: any) => {
    if (allowedExtensions && allowedExtensions.length > 0) {
      const ext = file.name.match(/(?:\.([^.]+))?$/)[1];

      const isValid = allowedExtensions.some((x) => x === `.${ext}`);

      setErrorValidation(
        isValid
          ? null
          : t("fileExtensionNotAllowed", {
              extensions: allowedExtensions.join(", "),
            })
      );

      return isValid;
    }

    return true;
  };

  useEffect(() => {
    if (errorValidate === true) {
      setErrorValidation(t("fileRequired"));
    } else {
      setErrorValidation(null);
    }
  }, [errorValidate]);

  return (
    <div
      className={`${styles.fileUploadWrapper} ${
        appendMode ? styles.multiple : ""
      } ${customClassName || ""}`}
      onDragEnter={handleDrag}
      onSubmit={(e) => e.preventDefault()}
    >
      <input
        ref={inputRef}
        type="file"
        id="input-file-upload"
        multiple={false}
        onChange={handleChange}
        className={styles["input-file-upload"]}
        accept={allowedExtensions ? allowedExtensions.join(",") : "*"}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <label
        id="label-file-upload"
        htmlFor="input-file-upload"
        className={`${styles["label-file-upload"]} ${
          dragActive ? styles["drag-active"] : ""
        } ${isReadOnly ? styles.disabled : ""}`}
      >
        <div>
          <CloudUploadIcon />
          <strong>{t(title || "uploadDraft")}</strong>
          <p>{t("or")}</p>
          <p>{t("dragAndDropHere")}</p>
        </div>
      </label>
      {dragActive && (
        <div
          id="drag-file-element"
          className={styles["drag-file-element"]}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        ></div>
      )}
      <Button
        className={styles["upload-button"]}
        onClick={onButtonClick}
        disabled={isReadOnly}
      >
        {t("upload")}
      </Button>
      {selectedFiles && (
        <List>
          {selectedFiles.map((f, index) => {
            const fileName = f.language
              ? `[${f.language}] - ${f.file.name}`
              : f.file.name;

            return (
              <ListItem key={f.language || f.file.name} title={fileName}>
                <ListItemIcon>
                  <UploadFileIcon />
                </ListItemIcon>
                <ListItemText>{fileName}</ListItemText>
                {appendMode && (
                  <ListItemIcon>
                    <Tooltip title={t("delete")} disableInteractive arrow>
                      <IconButton
                        onClick={() => {
                          onRemoveClick(index);
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemIcon>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
      <FormHelperText error>{errorValidation}</FormHelperText>
    </div>
  );
};

export default FileUploader;
